import React, { useState } from 'react';
import { Wizard, WizardStep, useStepController } from './Wizard/Wizard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConnectionType } from '../../../services/FormsService/models/ConnectionType';
import { FormType } from '../../../services/FormsService/models/FormType';
import * as FormService from '../../../services/FormsService/forms.service';
import { FormControl } from 'floorplan/components/v2/Form';
import { Button } from 'floorplan/components/v2/Button';
import TokenService from '../../../services/token.service';
import cx from 'classnames';
import NumberedQuestionWizardStep from './NumberedQuestionWizardStep';

import './SurveyContent.scss';

const PostInspectionSurveyContent = (props) => {    
    const stepController = useStepController();
    const [formSucceeded, updateFormSucceeded] = useState(false);
    const [formFailed, updateFormFailed] = useState(false);
    const [submitting, updateSubmitting] = useState(false);

    const [form, updateForm] = useState({
        schedulingDifficulty: 0,
        availableTimeSlotsSatisfaction: 0,
        informedConfidence: 0,
        reminderHelpfulness: 0,
        representativePunctuality: 0,
        representativeProfessionalism: 0,
        representativeExplaination: 0,
        offerpadSolutionsExplaination: 0,
        representativeOverallRating: 0,
        offerpadComments: ''
    });

    const [formErrors, updateFormErrors] = useState({
        schedulingDifficulty: false,
        availableTimeSlotsSatisfaction: false,
        informedConfidence: false,
        reminderHelpfulness: false,
        representativePunctuality: false,
        representativeProfessionalism: false,
        representativeExplaination: false,
        offerpadSolutionsExplaination: false,
        representativeOverallRating: false
    });

    const updateField = (field, value) => {
        updateForm({ ...form, [field]: value });
        updateFormErrors({ ...formErrors, [field]: false });
    };

    const validate = (field) => {
        if (form[field] || form[field] === 0) {
            stepController.incrementStep();
        }
        else {
            updateFormErrors({ ...formErrors, [field]: true });
        }
    };

    const navigateAway = () => {
        const loggedIn = TokenService.getToken();
        if (loggedIn) {
            window.location.href = `${process.env.RAZZLE_CONNECT_URL}/sell/overview?transactionId=${props.acquisitionId}`
        } else {
            window.location.href = `https://www.offerpad.com/`;
        }
    };

    const submitForm = () => {
        if (submitting) {
            return;
        }
        updateSubmitting(true);
        FormService.submitForm({
            connectionId: props.acquisitionId,
            connectionType: ConnectionType.HelixEntity,
            formType: FormType.PostInspectionSurvey,
            items: Object.keys(form).map((key) => {
                return {
                    name: key,
                    value: form[key],
                };
            }),
        })
            .then(
                () => {
                    updateFormSucceeded(true);
                },
                () => {
                    updateFormFailed(true);
                })
            .catch(() => {
                updateFormFailed(true);
            })
            .finally(() => {
                updateSubmitting(false);
                stepController.incrementStep();
            });
    };

    const NavigationButtons = () => {
        return (
            <div className={cx({ 'btn-wrapper': true, 'd-none': submitting })} >
                {
                    (!formSucceeded && !formFailed && stepController.currentStep > 0) ?
                        <button
                            id="post-inspection-survey-previous"
                            className="btn-survey-previous"
                            onClick={stepController.decrementStep}
                        >
                            <FontAwesomeIcon icon={['fas', 'long-arrow-left']} />
                            {' Back'}
                        </button>
                        :
                        <button id="post-inspection-navigate-away" className="btn-survey-previous" onClick={navigateAway}>
                            <FontAwesomeIcon icon={['fas', 'times']} />
                            {' Close'}
                        </button>
                }
            </div>
        )
    };

    return (
        <div>
            <NavigationButtons />
            <Wizard stepController={stepController} >                
                <WizardStep>
                    <div className="step-wrapper">                        
                        <div className="modal-main">
                            <div>
                                <img src={require('@assets/op-logo.svg')} className="logo" />                                
                            </div>
                            <div style={{ marginLeft: "8px" }}>
                                <h1 className="d-none d-md-block" style={{ margin: 0, padding: 0, marginTop: "8px", marginBottom: "8px" }} >Welcome</h1>
                                <div className="highlight">
                                    We value your feedback! Please take a few moments to share your thoughts about your recent in-home walkthrough.
                                    Your input helps us improve our services and provide the best possible experience.
                                </div>
                            </div>                            
                            <div className="btn-wrapper pt-4">
                                <Button id="post-inspection-survey-begin" large onClick={stepController.incrementStep}>
                                    Let's Begin
                                </Button>
                            </div>
                        </div>
                    </div>
                </WizardStep>

                <NumberedQuestionWizardStep
                    question="How easy was it to schedule your in-home walkthrough?"
                    form={form}
                    field="schedulingDifficulty"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Very difficult"                    
                    maxLabel="Very easy"
                    onValidate={() => validate('schedulingDifficulty')}
                    onUpdate={(option) => updateField('schedulingDifficulty', option)}
                />

                <NumberedQuestionWizardStep
                    question="How satisfied were you with the availability of in-home walkthrough time slots?"
                    form={form}
                    field="availableTimeSlotsSatisfaction"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Very dissatisfied"
                    maxLabel="Very satisfied"
                    onValidate={() => validate('availableTimeSlotsSatisfaction')}
                    onUpdate={(option) => updateField('availableTimeSlotsSatisfaction', option)}
                />

                <NumberedQuestionWizardStep
                    question="How well was the in-home walkthrough process explained beforehand?"
                    form={form}
                    field="informedConfidence"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Not well at all"
                    maxLabel="Very well"
                    onValidate={() => validate('informedConfidence')}
                    onUpdate={(option) => updateField('informedConfidence', option)}
                />

                <NumberedQuestionWizardStep
                    question="How helpful were the reminders or updates you received about your appointment?"
                    form={form}
                    field="reminderHelpfulness"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Not helpful at all"
                    maxLabel="Very helpful"
                    onValidate={() => validate('reminderHelpfulness')}
                    onUpdate={(option) => updateField('reminderHelpfulness', option)}
                />

                <NumberedQuestionWizardStep
                    question="Was the representative punctual?"
                    form={form}
                    field="representativePunctuality"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Very late"
                    maxLabel="Arrived right on time"
                    onValidate={() => validate('representativePunctuality')}
                    onUpdate={(option) => updateField('representativePunctuality', option)}
                />

                <NumberedQuestionWizardStep
                    question="How would you rate the professionalism of the representative?"
                    form={form}
                    field="representativeProfessionalism"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Poor"
                    maxLabel="Excellent"
                    onValidate={() => validate('representativeProfessionalism')}
                    onUpdate={(option) => updateField('representativeProfessionalism', option)}
                />

                <NumberedQuestionWizardStep
                    question="How well did the representative explain the walkthrough process and next steps?"
                    form={form}
                    field="representativeExplaination"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Not well at all"
                    maxLabel="Very well"
                    onValidate={() => validate('representativeExplaination')}
                    onUpdate={(option) => updateField('representativeExplaination', option)}
                />

                <NumberedQuestionWizardStep
                    question="Did the representative clearly explain Offerpad's solutions and how they work?"
                    form={form}
                    field="offerpadSolutionsExplaination"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Not at all"
                    maxLabel="Very clearly"
                    onValidate={() => validate('offerpadSolutionsExplaination')}
                    onUpdate={(option) => updateField('offerpadSolutionsExplaination', option)}
                />

                <NumberedQuestionWizardStep
                    question="How would you rate the overall experience with the representative?"
                    form={form}
                    field="representativeOverallRating"
                    error={formErrors}
                    errorText="Please select a number"
                    min={1}
                    max={5}
                    minLabel="Poor"
                    maxLabel="Excellent"
                    onValidate={() => validate('representativeOverallRating')}
                    onUpdate={(option) => updateField('representativeOverallRating', option)}
                />
               
                <WizardStep>
                    <div className="step-wrapper">
                        <div className="modal-main">
                            <div>
                                <img src={require('@assets/op-logo.svg')} className="logo" />
                            </div>
                            <div className="highlight" style={{ margin: "8px" }}>
                                Do you have any additional comments or suggestions to improve our in-home walkthrough process? 
                            </div>
                            <div style={{ margin: "8px" }}>
                                <FormControl
                                    id='offerpadComments'
                                    name='offerpadComments'
                                    placeholder="Tell us more"
                                    value={form.offerpadComments}
                                    onChange={(e) =>
                                        updateForm({
                                            ...form, offerpadComments: e.target.value
                                        })}
                                >
                                    <textarea
                                        name="offerpadComments"
                                        rows={4}
                                    ></textarea>
                                </FormControl>
                                <div className="btn-wrapper pt-5 new-pull-right">
                                    <Button
                                        id="post-inspection-submit-form"
                                        large
                                        disabled={submitting}
                                        onClick={submitForm}
                                    >
                                        {(submitting ? 'Submitting...' : 'Submit survey')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </WizardStep>

                <WizardStep>
                    <div className="p-2">
                        <div className="survey-end">
                            {
                                formSucceeded &&
                                <h3>
                                    Thank you for taking our mini-survey. Your feedback is valuable and appreciated!
                                </h3>
                            }
                            {
                                formFailed &&
                                <h3>
                                    We encountered an issue while submitting your survey. Please try again later.
                                </h3>
                            }
                            <div className="d-flex justify-content-center">
                                <img src={require('@assets/illustrations/survey_finished_icon.svg')} />
                            </div>
                        </div>
                    </div>
                </WizardStep>
            </Wizard>
        </div>
    );
}

export default PostInspectionSurveyContent;
